/**
 * Appointment-type-agnostic slot holding.
 *
 * A hold reserves a specific practitioner's time while something else finishes
 * — an account being created, a form being completed — and is confirmed into a
 * real Appointment or left to expire.
 *
 * Nothing here is specific to an appointment type. Slot length and eligible
 * practitioner roles are read from the ActivityDefinition at request time.
 */

/** Identifier carried by both the tentative hold Slot and the booked Slot. */
export const SLOT_HOLD_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/slot-hold';

export const DEFAULT_SLOT_HOLD_TTL_MINUTES = 15;

export interface HoldSlotInput {
  /** Code from the appointment-types CodeSystem identifying which kind of visit is being held. */
  appointmentType: string;
  /** ISO datetime of the chosen slot start. */
  startTime: string;
  /**
   * "Practitioner/uuid" — hold this provider's time specifically.
   *
   * Omit it and the hold goes to whoever is least booked that day, which is right
   * for a caller that only asked for a time. A caller offering a provider per
   * button has to send it, or the grid promises a provider the hold may not honour.
   */
  practitionerReference?: string;
  /** Overrides DEFAULT_SLOT_HOLD_TTL_MINUTES. */
  ttlMinutes?: number;
}

export interface HoldSlotResult {
  /** Opaque handle; also the Slot identifier value. */
  holdId: string;
  /** ISO datetime after which the hold is void. */
  expiresAt: string;
  startTime: string;
  endTime: string;
  /** "Practitioner/uuid" — whose time was held, whether or not the caller asked for them. */
  practitionerReference: string;
}

export interface BookHeldSlotInput {
  holdId: string;
  /** "Patient/uuid" — by now the account exists. */
  patientId: string;
  /** IANA timezone, defaults to UTC. */
  timezone?: string;
  /** Defaults to "VR". */
  encounterClass?: 'VR' | 'AMB';
  /** "Location/uuid" — required when encounterClass is "AMB". */
  locationId?: string;
}
